import { supabase } from "@/lib/supabase"
import type { Collection, Collaborator } from "@/lib/store/usePickStore"

export const fetchCollections = async (): Promise<{ collections: Collection[], collaborators: Record<string, Collaborator[]> }> => {
    const { data: { session } } = await supabase.auth.getSession()
    const user = session?.user ?? (await supabase.auth.getUser()).data.user
    if (!user) throw new Error('Not authenticated')

    // 1. Fetch owned
    const { data: ownedData, error: ownedError } = await supabase
        .from('collections')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })

    if (ownedError) throw ownedError

    // 2. Fetch collaborated
    const { data: collabData, error: collabError } = await supabase
        .from('collection_collaborators')
        .select('collection_id, collections(*)')
        .eq('user_id', user.id)

    if (collabError) console.error('Error fetching collaborated collections:', collabError)

    const collabCollections = (collabData || [])
        .map((row: any) => row.collections)
        .filter(Boolean)

    const allRaw = [...(ownedData || []), ...collabCollections]
    
    // Deduplicate
    const seen = new Set<string>()
    const uniqueRaw = allRaw.filter(c => { const ok = !seen.has(c.id); seen.add(c.id); return ok })

    // 3. Fetch profiles for owners
    const ownerIds = Array.from(new Set(uniqueRaw.map(c => c.user_id).filter(Boolean)))
    let profileMap: Record<string, { display_name: string; avatar_url: string; avatar_color_index: number }> = {}

    if (ownerIds.length > 0) {
        const { data: profiles } = await supabase
            .from('profiles')
            .select('id, display_name, avatar_url, avatar_color_index')
            .in('id', ownerIds)

        if (profiles) {
            profileMap = Object.fromEntries(profiles.map(p => [p.id, p]))
        }
    }

    const formattedCollections: Collection[] = uniqueRaw.map((c: any) => ({
        id: c.id,
        userId: c.user_id,
        name: c.name,
        icon: c.icon,
        color: c.color,
        imageUrl: c.image_url,
        coverEmoji: c.cover_emoji,
        coverColor: c.cover_color,
        ownerName: profileMap[c.user_id]?.display_name,
        ownerAvatarUrl: profileMap[c.user_id]?.avatar_url,
        ownerAvatarColorIndex: profileMap[c.user_id]?.avatar_color_index,
        count: 0,
        createdAt: c.created_at,
        updatedAt: c.updated_at,
        lastVisitedAt: c.last_visited_at,
        shareToken: c.share_token
    }))

    // 4. Fetch pick counts
    const collectionIds = formattedCollections.map(c => c.id)
    const newCollaborators: Record<string, Collaborator[]> = {}

    if (collectionIds.length > 0) {
        const { data: membershipData, error: countError } = await supabase
            .from('pick_memberships')
            .select('collection_id, pick_id, picks!inner(id)')
            .in('collection_id', collectionIds)

        if (!countError && membershipData) {
            const uniquePicksPerCollection: Record<string, Set<string>> = {}
            membershipData.forEach((row: any) => {
                if (!uniquePicksPerCollection[row.collection_id]) {
                    uniquePicksPerCollection[row.collection_id] = new Set<string>()
                }
                uniquePicksPerCollection[row.collection_id].add(row.pick_id)
            })
            formattedCollections.forEach(c => {
                c.count = uniquePicksPerCollection[c.id]?.size || 0
            })
        }

        // 5. Fetch previews (RPC)
        const { data: previewData } = await supabase
            .rpc('get_collection_previews', { collection_ids: collectionIds })

        if (previewData) {
            previewData.forEach((pd: { collection_id: string, preview_urls: string[] }) => {
                const coll = formattedCollections.find(c => c.id === pd.collection_id)
                if (coll && pd.preview_urls && pd.preview_urls.length > 0) {
                    coll.previewImages = pd.preview_urls
                }
            })
        }

        // 6. Fetch ALL collaborators
        const { data: collabRows } = await supabase
            .from('collection_collaborators')
            .select('collection_id, user_id, role, joined_at')
            .in('collection_id', collectionIds)

        const allUserIdsNeeded = Array.from(new Set([
            ...ownerIds,
            ...(collabRows?.map(r => r.user_id) || [])
        ]))

        let fullProfileMap = { ...profileMap } as any
        const missingProfileIds = allUserIdsNeeded.filter(id => !fullProfileMap[id])

        if (missingProfileIds.length > 0) {
            const { data: moreProfiles } = await supabase
                .from('profiles')
                .select('id, display_name, avatar_url, avatar_color_index')
                .in('id', missingProfileIds)

            if (moreProfiles) {
                moreProfiles.forEach(p => { fullProfileMap[p.id] = p })
            }
        }

        formattedCollections.forEach(c => {
            const ownerProf = fullProfileMap[c.userId]
            newCollaborators[c.id] = [{
                userId: c.userId,
                displayName: ownerProf?.display_name || 'Owner',
                avatarUrl: ownerProf?.avatar_url,
                avatarColorIndex: ownerProf?.avatar_color_index,
                role: 'owner',
                joinedAt: c.createdAt,
                isOwner: true
            }]
        })

        collabRows?.forEach(r => {
            const collectionOwnerId = formattedCollections.find(cn => cn.id === r.collection_id)?.userId
            if (r.user_id === collectionOwnerId) return
            const p = fullProfileMap[r.user_id]
            newCollaborators[r.collection_id].push({
                userId: r.user_id,
                displayName: p?.display_name || 'User',
                avatarUrl: p?.avatar_url,
                avatarColorIndex: p?.avatar_color_index,
                role: r.role,
                joinedAt: r.joined_at,
                isOwner: false
            })
        })
    }

    return { collections: formattedCollections, collaborators: newCollaborators }
}
